// Single source of truth for HSS Application fields.
// Used by the member Profile/Account tabs (editable) and the Admin application view (read-only).
// Field keys match exactly what the Application form writes to the applications collection.

const HOBBIES = [
  'Gardening', 'Television/Movies', 'Card games', 'Painting', 'Crafts', 'Gaming',
  'Music playing', 'Bowls', 'Table tennis', 'Pool/Snooker/Billiards',
  'Restaurants/Bistros/Cafes', 'Bands - listening', 'Walking/Running', 'Swimming',
  'Cooking/Baking', 'Chess/Draughts/Go', 'Puzzles/Crosswords', 'Sewing/Embroidery',
  'Computers/Internet/Web', 'Music listening', 'Cars/Car maintenance',
  'Badminton', 'Tennis or similar', 'Dancing', 'Bars - drinking', 'Gym/Yoga',
  'Bushwalking', 'Karaoke', 'Kayaking', 'Sailing', 'Surfing'
];

const USEFUL_ITEMS = [
  'Car', 'Bicycle', 'Motor bike', 'Microwave', 'Oven', 'Fridge', 'Freezer', 'TV',
  'Music equipment', 'Washing Machine', 'Dryer', 'Mower', 'Whipper snipper',
  'Garden tools', 'Hand tools', 'Power tools', 'Lounge furniture', 'Outdoor furniture'
];

export const SMOKE_FREE_STATEMENT = 'All HSS properties are smoke-free.';

export const APPLICATION_SECTIONS = [
  {
    key: 'personal',
    title: 'Personal Details',
    note: 'A couple shares one profile — enter both partners below.',
    fields: [
      { key: 'shared_housing_type', label: 'Shared Housing Type', type: 'select', options: [
        ['single_female', 'Single Female — Female Community'],
        ['single_male', 'Single Male — Male Community'],
        ['couples', 'Couples — Couples (mixed gender) Community'],
      ] },
      { key: 'first_name', label: 'Given Name', type: 'text' },
      { key: 'last_name', label: 'Family Name', type: 'text' },
      { key: 'preferred_name', label: 'Preferred Name', type: 'text' },
      { key: 'age', label: 'Age', type: 'number' },
      { key: 'gender', label: 'Gender', type: 'select', options: [['female', 'Female'], ['male', 'Male'], ['non_binary', 'Non-binary']] },
      { key: 'phone', label: 'Phone', type: 'text' },
      { key: 'state', label: 'State', type: 'select', options: ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'].map(s => [s, s]) },
      { key: 'city', label: 'City/Town', type: 'text' },
      { key: 'postcode', label: 'Postcode', type: 'text' },
      { key: 'preferred_town_region', label: 'Preferred Town/Region', type: 'text' },
    ],
  },
  {
    key: 'partner',
    title: 'Partner 2 (couples only)',
    couplesOnly: true,
    fields: [
      { key: 'partner_first_name', label: 'Given Name', type: 'text' },
      { key: 'partner_last_name', label: 'Family Name', type: 'text' },
      { key: 'partner_preferred_name', label: 'Preferred Name', type: 'text' },
      { key: 'partner_age', label: 'Age', type: 'number' },
      { key: 'partner_gender', label: 'Gender', type: 'select', options: [['female', 'Female'], ['male', 'Male'], ['non_binary', 'Non-binary']] },
    ],
  },
  {
    key: 'financial',
    title: 'Financial Circumstances',
    note: 'Confidential — used only to keep housing affordable.',
    fields: [
      { key: 'income_sources', label: 'Income Sources', type: 'multi', options: [
        ['age_pension', 'Age Pension'], ['disability_pension', 'Disability Support Pension'],
        ['superannuation', 'Superannuation'], ['part_time_employment', 'Part-time employment'],
        ['investments', 'Investment income'], ['family_friends', 'Family & friends'],
        ['loans', 'Loans'], ['other', 'Other'],
      ] },
      { key: 'maximum_weekly_rent', label: 'Maximum Weekly Rent ($)', type: 'number' },
    ],
  },
  {
    key: 'lifestyle',
    title: 'Lifestyle',
    note: 'Shared by both partners if a couple. ' + SMOKE_FREE_STATEMENT,
    fields: [
      { key: 'has_pets', label: 'Has Pets', type: 'bool' },
      { key: 'pet_details', label: 'Pet Details', type: 'text' },
      { key: 'daily_routine', label: 'Daily Routine', type: 'multi', options: [
        ['early_riser', 'Early riser (up before 7am)'], ['morning_person', 'Morning person (up 7-9am)'],
        ['late_riser', 'Late riser (up after 9am)'], ['night_owl', 'Night owl (active in evenings)'],
        ['early_bed', 'Early to bed'],
      ] },
      { key: 'hobbies', label: 'Hobbies and Interests', type: 'multi', options: HOBBIES.map(h => [h, h]) },
      { key: 'other_hobbies', label: 'Other Hobbies or Interests', type: 'text' },
    ],
  },
  {
    key: 'household',
    title: 'Household & Community',
    fields: [
      { key: 'household_responsibilities', label: 'Household Responsibilities', type: 'multi', options: [
        'Happy to help with cleaning shared areas',
        'Happy to help with garden care',
        'Happy to help with property maintenance',
        'Happy to help with grocery shopping',
        'Happy to help with transport for our community (with shared costs)',
      ].map(v => [v, v]) },
      { key: 'appreciate_help_with_tasks', label: 'Would appreciate help with some tasks', type: 'bool' },
      { key: 'happy_to_share_transport', label: 'Happy to share car driving & expenses', type: 'bool' },
      { key: 'cooking_preference', label: 'Cooking Preference', type: 'select', options: [
        ['own_cooking', 'Prefer to do all my/our own cooking'],
        ['occasional_communal', 'Happy with occasional communal cooking & meals'],
        ['regular_communal', 'Happy with regular communal cooking & meals'],
        ['frequent_communal', 'Happy with frequent communal cooking & meals'],
      ] },
      { key: 'visitors_frequency', label: 'Entertaining family & friends', type: 'select', options: [
        ['never', 'Never/NA'], ['few_months', 'Every few months maybe'], ['monthly', 'Every month probably'],
        ['weekly', 'Every week probably'], ['often', 'Often'],
      ] },
      { key: 'overnight_guests', label: 'Overnight Guests', type: 'select', options: [
        ['never', 'Never'], ['rarely', 'Rarely'], ['monthly', 'Monthly'], ['weekly', 'Weekly'], ['more_often', 'More often'],
      ] },
      { key: 'religious_cultural_requirements', label: 'Religious & Cultural Requirements', type: 'textarea' },
    ],
  },
  {
    key: 'health',
    title: 'Health & Accessibility',
    note: 'Kept strictly confidential.',
    fields: [
      { key: 'medical_conditions', label: 'Medical Conditions', type: 'multi', options: [
        'Diabetes', 'Arthritis', 'Asthma', 'Heart Condition', 'High Blood Pressure', 'Dementia', 'Other',
      ].map(v => [v, v]) },
      { key: 'mobility_status', label: 'Disability / Mobility', type: 'select', options: [
        ['fully_mobile', 'Fully mobile for normal activities'], ['some_difficulty', 'Walk with some difficulty'],
        ['walking_frame', 'Walking frame'], ['disability_scooter', 'Disability scooter'], ['other', 'Other'],
      ] },
      { key: 'accessibility_requirements', label: 'Accessibility Requirements', type: 'multi', options: [
        'Ground Floor Required', 'Grab Rails Required', 'Lift Access Required',
      ].map(v => [v, v]) },
      { key: 'other_accessibility_needs', label: 'Other Accessibility Needs', type: 'textarea' },
    ],
  },
  {
    key: 'safety',
    title: 'Safety & References',
    fields: [
      { key: 'police_check_status', label: 'Police Check Status', type: 'select', options: [
        ['have_current', 'I have a current police check'], ['can_obtain', 'I can obtain one'], ['need_help', 'I need help obtaining one'],
      ] },
      { key: 'can_provide_references', label: 'Can provide references', type: 'bool' },
    ],
  },
  {
    key: 'preferences',
    title: 'Your Preferences',
    fields: [
      { key: 'age_range_preference', label: 'Age Range Preference', type: 'select', options: [['50s', '50s'], ['60s', '60s'], ['70s', '70s'], ['80s+', '80s+']] },
      { key: 'household_size_preference', label: 'Household Size Preference', type: 'select', options: [['2', '2 people'], ['3', '3 people'], ['4', '4 people'], ['5+', '5+ people']] },
      { key: 'enjoy_checkboxes', label: 'Things I would enjoy', type: 'multi', options: [
        'Community', 'Garden', 'Cooking', 'Shared Meals', 'Music', 'Games', 'Entertainment',
      ].map(v => [v, v]) },
      { key: 'other_enjoy', label: 'Other things I would enjoy', type: 'text' },
      { key: 'hate_checkboxes', label: 'Things I would NOT enjoy', type: 'multi', options: [
        'Uncleanliness', 'Untidiness', 'Foul language', 'Unkindness', 'Selfishness',
      ].map(v => [v, v]) },
      { key: 'other_hate', label: 'Other things I would not enjoy', type: 'text' },
      { key: 'deal_makers', label: 'Deal Makers', type: 'textarea' },
      { key: 'non_negotiable_conditions', label: 'Non-Negotiable Conditions', type: 'textarea' },
      { key: 'dealbreakers', label: 'Dealbreaker Behaviours', type: 'multi', options: [
        'Heavy drinking', 'Aggressive behavior', 'Untidy/messy habits',
        'Overnight guests', 'Loud noise late at night', 'Drug use', 'Poor hygiene',
        'Certain types of pets', 'Unpredictable schedule',
      ].map(v => [v, v]) },
      { key: 'other_dealbreakers', label: 'Other Dealbreakers', type: 'textarea' },
    ],
  },
  {
    key: 'compatibility',
    title: 'Household Compatibility',
    fields: [
      { key: 'hc_q1', label: 'Household atmosphere you hope to find', type: 'textarea' },
      { key: 'hc_q2', label: 'A neighbourhood/community you enjoyed', type: 'textarea' },
      { key: 'hc_q3', label: 'Concerns about living with people you have not met', type: 'textarea' },
      { key: 'hc_q4', label: 'Most positive and negative changes in Australia', type: 'textarea' },
      { key: 'hc_q5', label: 'Where you get your daily news', type: 'textarea' },
      { key: 'hc_q6', label: 'A belief others may disagree with', type: 'textarea' },
      { key: 'hc_q7', label: 'An issue you changed your view on', type: 'textarea' },
      { key: 'hc_q8', label: 'Issues that worry you most', type: 'textarea' },
      { key: 'hc_q9', label: 'Most difficult thing about how the world is changing', type: 'textarea' },
    ],
  },
  {
    key: 'items',
    title: 'Useful Items',
    fields: [
      { key: 'useful_items', label: 'Items you could bring', type: 'multi', options: USEFUL_ITEMS.map(i => [i, i]) },
      { key: 'other_items', label: 'Other Items', type: 'text' },
      { key: 'interested_member_support_role', label: 'Interested in a Member Support role? (1x6hr day/week, award rates, no Pension impact, on-site, phone & email only)', type: 'select', options: [['yes', "Yes, I'm interested"], ['maybe', 'Maybe — tell me more'], ['no', 'No, not interested']] },
    ],
  },
];

// Every editable application key — used to whitelist on the backend too.
export const ALL_APPLICATION_KEYS = APPLICATION_SECTIONS.flatMap(s => s.fields.map(f => f.key));

export const labelFor = (field, value) => {
  if (value == null || value === '') return '';
  if (field.type === 'bool') return value === true ? 'Yes' : value === false ? 'No' : '';
  if (field.options) {
    const map = Object.fromEntries(field.options);
    if (Array.isArray(value)) return value.map(v => map[v] || v).join(', ');
    return map[value] || value;
  }
  return Array.isArray(value) ? value.join(', ') : String(value);
};

export const visibleSections = (app) => {
  const isCouple = app?.shared_housing_type === 'couples';
  return APPLICATION_SECTIONS.filter(s => !s.couplesOnly || isCouple);
};
